import { EnergyFlowChartResponse } from '@/api/plustekSankey';
import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { getNodeMaxDepth, getSankeyHeight } from '../UnitSankey';
import { apiV2EnergyAnalysisGetDifferenceDescPost } from '@maxtropy/device-customer-apis-v2';
import { useMemo } from 'react';

interface Props {
  isMedium: boolean;
  data?: EnergyFlowChartResponse;
  physicalUnitName?: string;
}

const color = ['var(--mx-diagram15-color)', 'var(--mx-diagram6-color)'];

const SankeyCharts: React.FC<Props> = ({ isMedium, data, physicalUnitName }) => {
  const { data: differenceDesc } = useRequest(apiV2EnergyAnalysisGetDifferenceDescPost);
  const theme = useTheme();

  const chartHeight = useMemo(() => {
    const height = getSankeyHeight(data?.nodes ?? []);
    return height;
  }, [data]);

  const getChartOption = () => {
    return {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        borderColor: 'transparent',
        textStyle: {
          fontSize: 12,
        },
        formatter: (v: any) => {
          let res = '';
          if (v.dataType === 'node') {
            res = `<div>
          <p>${v.data.energyUnitName}</p>
          <p style="color: var(--mx-text-base-color); margin-bottom: 0px;" >${Number(v.value).toFixed(
            2
          )}${physicalUnitName}</p>
          </div>`;
          }
          if (v.dataType === 'edge') {
            const sourceName = data?.nodes?.find(i => i.key === v.data.source)?.energyUnitName;
            const targetName = data?.nodes?.find(i => i.key === v.data.target)?.energyUnitName;
            res = `<div>
          <p>${sourceName}——${targetName}</p>
          <p style="color: var(--mx-text-base-color); margin-bottom: 0px;" >${Number(v.value).toFixed(
            2
          )}${physicalUnitName}</p>
          </div>`;
          }

          return res;
        },
      },
      series: [
        {
          type: 'sankey',
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
          nodeGap: 2,
          layoutIterations: 0, // 解决能流图衔接问题
          nodeAlign: 'justify',
          animation: false,
          label: {
            color: 'var(--mx-text-base-color)',
            rich: {
              a: {
                color: 'var(--mx-error-color)',
              },
              b: {
                color: 'var(--mx-success-color)',
              },
            },
            formatter: (v: any) => {
              if (v.data.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗')) {
                return `{a|${v.data.energyUnitName}}`;
              }
              if (v.data.energyUnitName === differenceDesc?.upstreamGreaterThanDownstream || '用能损耗') {
                return `{b|${v.data.energyUnitName}}`;
              }
              return `${v.data.energyUnitName}`;
            },
          },
          labelLayout: {
            hideOverlap: true,
          },
          draggable: false,
          data: data?.nodes?.map(i => ({
            ...i,
            itemStyle: {
              color:
                i.energyUnitName === (differenceDesc?.upstreamGreaterThanDownstream || '用能损耗')
                  ? 'var(--mx-success-color)'
                  : i.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗')
                  ? 'var(--mx-error-color)'
                  : i.depth === 0
                  ? color[0]
                  : color[1],
            },
            label: {
              position: i.depth === getNodeMaxDepth(data?.nodes) ? 'left' : 'right',
            },
          })),
          links: data?.links?.map(i => {
            return {
              ...i,
              lineStyle: {
                color: 'gradient',
                opacity: 0.3,
                curveness: 0.5,
              },
            };
          }),
        },
      ],
    };
  };
  return (
    <div
      style={{
        width: '100%',
        height: 177,
        overflow: 'auto',
      }}
    >
      <div style={{ width: isMedium ? '976px' : '542px', height: chartHeight }}>
        <EchartsWithTheme
          style={{
            width: '100%',
            height: '100%',
          }}
          option={getChartOption()}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default SankeyCharts;
